import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  getPreferredColorScheme,
  getTelegramWebApp,
  syncTelegramThemeColors,
} from "../lib/telegram";
import {
  applyDocumentTheme,
  getStoredThemePreference,
  resolveTheme,
  storeThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from "../lib/theme";

interface ThemeContextValue {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(getStoredThemePreference);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(getStoredThemePreference(), getPreferredColorScheme()),
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const webApp = getTelegramWebApp();

    const updateTheme = () => {
      const nextTheme = resolveTheme(preference, getPreferredColorScheme());
      setResolvedTheme(nextTheme);
      applyDocumentTheme(nextTheme);
      syncTelegramThemeColors(nextTheme);
    };

    updateTheme();
    mediaQuery.addEventListener?.("change", updateTheme);
    webApp?.onEvent?.("themeChanged", updateTheme);

    return () => {
      mediaQuery.removeEventListener?.("change", updateTheme);
      webApp?.offEvent?.("themeChanged", updateTheme);
    };
  }, [preference]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      resolvedTheme,
      setPreference: (nextPreference) => {
        storeThemePreference(nextPreference);
        setPreferenceState(nextPreference);
      },
    }),
    [preference, resolvedTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used inside ThemeProvider");
  return context;
}
